/**
 * Is there a finger on this screen, and is it the only thing driving?
 *
 * Two different questions, and conflating them is how every "mobile detect"
 * goes wrong. A Surface and a touchscreen ThinkPad both report a touchscreen
 * while the person is using a mouse; an iPad reports a Macintosh user agent
 * while having no mouse at all. So nothing here is decided by one signal:
 *
 *   - `touch` — the device can report a touch at all. Enough to *offer* the
 *     on-screen pad, never enough to force it on.
 *   - `coarse` — the primary pointer is a fingertip and nothing hovers. This is
 *     the pair of media queries that a mouse falsifies, which is exactly what
 *     we want it to do.
 *   - `mobile` — a phone or a tablet, as far as anything can honestly tell.
 *
 * The user agent string is consulted last and only as corroboration, because it
 * is the one signal a browser will lie about on purpose.
 *
 * All of it is re-evaluated on media query changes: a detachable keyboard or a
 * paired mouse flips `pointer: coarse` mid-session, and the pad should follow
 * the person rather than whatever was true when the tab opened.
 */

export interface TouchProfile {
  /** Touches can be reported. The pad is *offered* on this alone. */
  readonly touch: boolean;
  /** The primary pointer is a fingertip, and nothing hovers. */
  readonly coarse: boolean;
  /** A phone or a tablet. */
  readonly mobile: boolean;
  /** Every check that answered yes — for the log and for `__retro`. */
  readonly signals: readonly string[];
}

/** The queries worth re-checking, because any of them can flip mid-session. */
const QUERIES = ['(pointer: coarse)', '(any-pointer: coarse)', '(hover: none)'] as const;

/** matchMedia is absent in some embedded webviews and throws on a bad query. */
function media(query: string): boolean {
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

/**
 * Phones, tablets and e-readers. Deliberately not a complete list and
 * deliberately not load-bearing: it only ever *adds* confidence to signals that
 * already agree, and iPadOS — which reports `Macintosh` here — is caught by
 * `maxTouchPoints` and the media queries instead.
 */
const UA_MOBILE = /Android|iPhone|iPod|iPad|Mobile|Silk|Kindle|PlayBook|BB10|Tablet|Opera Mini|IEMobile/i;

export function detectTouch(): TouchProfile {
  const signals: string[] = [];
  const note = (name: string, on: boolean): boolean => {
    if (on) signals.push(name);
    return on;
  };

  const points = note('maxTouchPoints', (navigator.maxTouchPoints ?? 0) > 0);
  const events = note('ontouchstart', 'ontouchstart' in window);
  const anyCoarse = note('any-pointer:coarse', media('(any-pointer: coarse)'));
  const primaryCoarse = note('pointer:coarse', media('(pointer: coarse)'));
  const noHover = note('hover:none', media('(hover: none)'));

  // Client Hints, where they exist, are the only signal here the browser
  // intends as an answer rather than as a side effect. Read defensively: it is
  // absent on every non-Chromium engine.
  const uaData = (navigator as { userAgentData?: { mobile?: unknown } }).userAgentData;
  const uaMobile = note('uaData.mobile', uaData?.mobile === true);
  const uaString = note('ua-string', UA_MOBILE.test(navigator.userAgent));

  // Three independent ways to have a digitiser. Any one of them is enough to
  // offer the pad; none of them is enough to turn it on.
  const touch = points || events || anyCoarse;
  // Both halves, because a touchscreen laptop matches `any-pointer: coarse`
  // while its mouse keeps `pointer: coarse` and `hover: none` false.
  const coarse = primaryCoarse && noHover;
  // Client Hints outright, or a touchscreen whose primary pointer really is a
  // finger, or a touchscreen that also names itself a phone.
  const mobile = uaMobile || (touch && (coarse || uaString));

  return { touch, coarse, mobile, signals };
}

/** True when the pad should start switched on rather than merely be offered. */
export function touchByDefault(profile: TouchProfile): boolean {
  return profile.touch && (profile.coarse || profile.mobile);
}

/**
 * Re-run detection whenever the answer could have changed.
 *
 * Pairing a mouse to a tablet, or docking a phone, flips these queries while
 * the tab is open. Firing on every one of them is cheap and means the toggle
 * appears or the pad steps aside without a reload.
 */
export function watchTouch(onChange: (profile: TouchProfile) => void): void {
  for (const query of QUERIES) {
    try {
      window.matchMedia(query).addEventListener('change', () => onChange(detectTouch()));
    } catch {
      // Safari below 14 has no addEventListener on MediaQueryList, and an
      // engine that cannot tell us is no worse than one that never changes.
    }
  }
}
